import React, { useEffect, useRef, useState } from "react";
import type { MarketField } from "../../server/markets/types";
import { dbk_usStates, dbk_normalizeState, dbk_sensitiveField, dbk_fieldVisible, type dbk_EstimateValues, type dbk_EstimateIssue } from "./usEstimateModel";

function StateSelector({ field, value, onChange, describedBy, invalid }: {
  field: MarketField; value: string; onChange: (value: string) => void; describedBy?: string; invalid: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const list = useRef<HTMLUListElement>(null);
  const choices = dbk_usStates.filter(([code, name]) => !value || code.toLowerCase().includes(value.toLowerCase()) || name.toLowerCase().includes(value.toLowerCase()));
  useEffect(() => {
    if (!open) return;
    function fitList() {
      const container = list.current;
      if (!container?.parentElement) return;
      const input = container.parentElement.getBoundingClientRect();
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop ?? 0;
      const bottom = top + (viewport?.height ?? window.innerHeight);
      const below = Math.max(0, bottom - input.bottom - 8);
      const above = Math.max(0, input.top - top - 8);
      const useAbove = below < 260 && above > below;
      container.dataset.placement = useAbove ? "above" : "below";
      container.style.setProperty("--dbk-state-available-height", `${Math.min(260, useAbove ? above : below)}px`);
    }
    fitList();
    window.addEventListener("resize", fitList);
    window.visualViewport?.addEventListener("resize", fitList);
    return () => {
      window.removeEventListener("resize", fitList);
      window.visualViewport?.removeEventListener("resize", fitList);
    };
  }, [open]);
  useEffect(() => {
    // Scroll only the list, never the page, when virtual keyboard focus moves.
    const container = list.current;
    const option = container?.children.item(active) as HTMLElement | null;
    if (!open || !container || !option) return;
    const bounds = container.getBoundingClientRect();
    const item = option.getBoundingClientRect();
    const top = bounds.top + container.clientTop;
    const bottom = top + container.clientHeight;
    if (item.top < top) container.scrollTop -= top - item.top;
    else if (item.bottom > bottom) container.scrollTop += item.bottom - bottom;
  }, [open, active]);
  function choose(code: string) { onChange(code); setOpen(false); setActive(-1); }
  return <div className="us-estimate__state-control">
    <input id={`us-${field.key}`} role="combobox" aria-autocomplete="list" aria-expanded={open}
      aria-controls="us-state-options" aria-activedescendant={open && active >= 0 && choices[active] ? `us-state-${choices[active]![0]}` : undefined}
      autoComplete="address-level1" value={value} required={field.required} maxLength={field.maxLength}
      aria-invalid={invalid} aria-describedby={describedBy} onFocus={() => { setOpen(true); setActive(-1); }}
      onBlur={() => { setOpen(false); setActive(-1); const code = dbk_normalizeState(value); if (code) onChange(code); }}
      onChange={event => { onChange(event.target.value); setOpen(true); setActive(-1); }}
      onKeyDown={event => {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault(); setOpen(true); setActive(index => Math.max(0, Math.min(choices.length - 1, index + (event.key === "ArrowDown" ? 1 : -1))));
        } else if (event.key === "Enter" && open) {
          event.preventDefault(); if (active >= 0 && choices[active]) choose(choices[active]![0]);
          else { const code = dbk_normalizeState(value); if (code) choose(code); }
        } else if (event.key === "Escape") { event.preventDefault(); setOpen(false); setActive(-1); }
      }} />
    {open && <ul ref={list} id="us-state-options" role="listbox" aria-label="U.S. states" className="us-estimate__state-options">
      {choices.map(([code, name], index) => <li key={code} id={`us-state-${code}`} role="option" aria-selected={active === index}
        onMouseDown={event => { event.preventDefault(); choose(code); }}>{name} ({code})</li>)}
      {!choices.length && <li role="presentation">Type a U.S. state name or abbreviation.</li>}
    </ul>}
  </div>;
}

export function dbk_UsEstimateFields({ fields, values, issues, onChange }: {
  fields: MarketField[]; values: dbk_EstimateValues; issues: dbk_EstimateIssue[]; onChange: (key: string, value: string | string[]) => void;
}) {
  return <div className="us-estimate__field-grid">
    {fields.filter(field => dbk_fieldVisible(field, values)).map(field => {
      const id = `us-${field.key}`;
      const error = issues.find(issue => issue.field === field.key)?.message;
      const describedBy = [field.help ? `${id}-help` : "", error ? `${id}-error` : ""].filter(Boolean).join(" ") || undefined;
      const raw = values[field.key]; const value = typeof raw === "string" ? raw : "";
      const required = field.required || (field.key === "accounting_software_other" && values.accounting_software_choice === "Other accounting software");
      const props = { id, required, "aria-invalid": !!error, "aria-describedby": describedBy };
      const sensitive = dbk_sensitiveField(field);
      return <div key={field.key} className={`us-estimate__field ${["business_website", "business_description"].includes(field.key) || field.kind === "multiselect" || sensitive ? "us-estimate__field--full" : ""} ${field.key === "us_state" ? "us-estimate__state" : ""}`}>
        {field.kind === "multiselect" ? <fieldset id={id} aria-describedby={describedBy} aria-invalid={!!error}>
          <legend>{field.label}{required ? " *" : ""}</legend>
          <div className="us-estimate__choices">{field.options?.map(option => <label key={option.value} className="us-estimate__choice">
            <input type="checkbox" aria-invalid={!!error} aria-describedby={describedBy} checked={Array.isArray(raw) && raw.includes(option.value)}
              onChange={event => { const selected = Array.isArray(raw) ? raw : [];
                onChange(field.key, event.target.checked ? [...selected, option.value] : selected.filter(v => v !== option.value)); }} />
            <span>{option.label}</span>
          </label>)}</div>
        </fieldset> : <>
          {sensitive && (field.kind === "ein" || /^(?:ein|tin|tax_identifier)$/i.test(field.key)) && <><label htmlFor={`${id}-type`}>Tax identifier type (optional)</label>
            <select id={`${id}-type`} value={typeof values.tax_identifier_type === "string" ? values.tax_identifier_type : field.key === "ein" ? "EIN" : ""}
              onChange={event => onChange("tax_identifier_type", event.target.value)}>
              <option value="">Choose an option</option><option value="EIN">EIN (Employer Identification Number)</option><option value="TIN">TIN (Taxpayer Identification Number)</option>
            </select></>}
          <label htmlFor={id}>{field.label}{required ? " *" : ""}</label>
          {field.key === "us_state" && field.kind !== "select" ? <StateSelector field={field} value={value}
            onChange={next => onChange(field.key, next)} describedBy={describedBy} invalid={!!error} /> :
            field.kind === "textarea" ? <textarea {...props} rows={5} value={value} maxLength={field.maxLength} onChange={event => onChange(field.key, event.target.value)} /> :
            field.kind === "select" ? <select {...props} value={value} onChange={event => onChange(field.key, event.target.value)}>
              <option value="">Choose an option</option>{field.options?.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select> : <input {...props} type={field.kind === "email" ? "email" : field.kind === "phone" ? "tel" : field.key === "business_website" ? "url" : "text"}
              value={value} maxLength={field.maxLength} autoComplete={sensitive ? "off" : undefined} inputMode={sensitive ? "numeric" : undefined}
              data-private={sensitive ? "true" : undefined} onChange={event => onChange(field.key, event.target.value)} />}
        </>}
        {field.help && <p id={`${id}-help`} className="us-estimate__help">{field.help}</p>}
        {error && <p id={`${id}-error`} className="us-estimate__error">{error}</p>}
      </div>;
    })}
  </div>;
}
